// English (unofficial translation). Proper nouns such as song titles stay in the original.
import type { SiteDict } from "./ja";
import type { RelativeDate } from "../../../utils/postMeta";

const en: SiteDict = {
  site: {
    titleSuffix: "!Legit - Unofficial Reol Fan Site",
    topTitle: "!Legit | Unofficial Reol fan site covering every song, live show and setlist",
    defaultDescription: (years: number) =>
      `An unofficial fan site for Reol. Live performance stats for every song, setlists from every show, a map of MV filming locations, a fan-type quiz and more — search ${years} years of activity in one place.`,
    breadcrumbHome: "Home",
  },
  header: {
    search: "Search",
    searchTitle: "Search songs, live shows and locations",
    toDark: "Switch to dark mode",
    toLight: "Switch to light mode",
    myPage: "My page",
    menu: "Menu",
    language: "Language",
  },
  footer: {
    officialSite: "Official site",
    goods: "Merch",
    disclaimer:
      "This is an unofficial fan site and is not affiliated with Reol or any official Reol organization. Music and videos are shown only through official embeds and links.",
    operator: "Run by (unofficial fan site)",
    privacy: "Privacy policy (Japanese)",
    mtNotice:
      "This page was machine-translated from Japanese. If you find any mistakes or unnatural wording, please let us know:",
    mtContact: "X @twilightplc",
  },
  video: {
    expand: "Expand the video",
    close: "Close the video",
    unmute: "Unmute",
    mute: "Mute",
    play: "Play the video",
  },
  home: {
    heroLine1Before: "Reol — ",
    heroLine1Accent: "where it began",
    heroLine1After: ",",
    heroLine2Indent: "Reol — ",
    heroLine2Accent: "where it's heading",
    heroLine2After: ".",
    heroEmMobile: 10.7,
    heroEmWide: 12.4,
    heroLead1: "Songs, live shows, setlists and filming locations, from the れをる era to today.",
    heroLead2: "An unofficial archive that also points you to official content.",
    ticketCta: "Tickets on the official site →",
    welcomeTitle: "New to Reol?",
    welcomeDesc: "Signature songs and an era-by-era guide, wherever you start.",
    liveTitle: "Go to a show",
    liveDesc: (n: number) => `Setlists from ${n} shows, plus a first-timer's guide.`,
    liveGuideLink: "Read the first-timer's guide →",
    dataTitle: "Dig into the data",
    dataDesc: (n: number) => `Performance counts, debuts and latest performances for all ${n} songs.`,
    timemachineTitle: "Time Machine",
    timemachineDesc: "Slide through the years to see how many songs, shows and performances there were at the time.",
    cgraphTitle: "Creator Relations",
    cgraphDesc: "See how the creators behind the songs are connected.",
    heatmapTitle: "Venue Map",
    heatmapDesc: "A heat map of shows by prefecture.",
    sorterTitle: "Song Sorter",
    sorterDesc: "Pick one of two songs at a time to crown your all-time favorite.",
    grammarTitle: "Setlist Grammar",
    grammarDesc: "Which songs follow which, where they tend to sit, and how it changes by year.",
    compareTitle: "Setlist Compare",
    compareDesc: "Compare two setlists: shared songs, differences and order changes.",
    tourHeatmapTitle: "Tour Heat Map",
    tourHeatmapDesc: "Every show of a tour side by side: which songs were played, and in what order.",
    similarityTitle: "Setlist Similarity Ranking",
    similarityDesc: "Pairs of shows from different tours with the most similar setlists.",
    postsTitle: "Related Posts",
    postsDesc: "Posts about songs, live shows and each performance, in chronological order.",
    quizTitle: "Reol Fan Type Quiz",
    quizDesc: "Answer 20 questions to find your fan type!",
    guideTitle: "Live Show Guide",
    guideDesc: "What to bring and wear, show etiquette, and tips for traveling to shows",
  },
  explore: {
    kicker: "EXPLORE — Main sections",
    discography: "Releases and every song",
    live: "Shows and setlists",
    place: "Filming location map",
    photo: "Photo gallery",
  },
  otd: {
    yesterday: "Yesterday",
    dayBeforeYesterday: "2 days ago",
    daysAgo: (n: number) => `${n} days ago`,
    tomorrow: "Tomorrow",
    dayAfterTomorrow: "In 2 days",
    daysLater: (n: number) => `In ${n} days`,
    releaseSuffix: " released",
    releaseTitle: (title: string) => `“${title}”`,
    kickerMonth: "ON THIS MONTH",
    kickerDay: "ON THIS DAY",
    yearsAgoToday: (years: number) => `${years} ${years === 1 ? "year" : "years"} ago today`,
    anniversaryIn: (when: string, years: number) => `${when}: ${years}-year anniversary`,
    anniversaryWas: (when: string, years: number) => `${when}: ${years}-year anniversary`,
    monthEntry: (md: string, years: number) => `${md} (${years} ${years === 1 ? "year" : "years"} ago)`,
    upcoming: (days: number, years: number) => `${years}-year anniversary in ${days} days`,
    seeAll: "See everything from this day →",
  },
  common: {
    postTiming: (kind: "live" | "release", rel: RelativeDate): string => {
      const ref = kind === "live" ? "the show" : "the release";
      if (rel.unit === "same") return kind === "live" ? "Day of the show" : "Release day";
      const unit = rel.unit === "day" ? "day" : rel.unit === "month" ? "month" : "year";
      return `${rel.amount} ${unit}${rel.amount === 1 ? "" : "s"} ${rel.direction === "before" ? "before" : "after"} ${ref}`;
    },
    postCategory: { 本人: "Reol", 公式: "Official", メディア: "Media" } as Record<string, string>,
    showMore: (n: number) => `Show ${n} more`,
    backToTop: "Back to top",
    backHome: "Back to HOME",
    loading: "Loading...",
    loadError: (err: string) => `Failed to load: ${err}`,
  },
  discography: {
    intro: "A timeline of Reol's releases, from singles and albums to cover videos (utattemita).",
    metaDescription:
      "Every Reol release on a timeline, from singles and albums to cover videos, with links for each song.",
    collectionLabel: "Collection (owned / watched)",
    collectionHint: "Tap ✓ on a card to mark it as owned or watched (saved only in this browser)",
    count: (n: number) => `${n} ${n === 1 ? "item" : "items"}`,
    unknownYear: "Unknown",
    helpFilter: "Tap a hashtag at the top to filter the list",
    helpFilterExample: "e.g. tap “#れをる” to show only releases under the name れをる",
    helpPlayBefore: "Tap",
    helpPlayAfter: "next to a title to play the video in the player at the top",
    helpOpenBefore: "Tap “",
    helpOpenAfter: " Open” to see the track list and links for each song",
    helpCommentBefore: "If “",
    helpCommentAfter: "” appears next to a song title, click it for Spotify links and track analysis",
    detail: "Details",
    detailTitle: "See the song page (performance stats, MV, related posts)",
    markOwned: "Mark as owned / watched",
    unmarkOwned: "Remove from owned",
    owned: "Owned",
    ownedCount: (n: number) => `${n} ${n === 1 ? "fan has" : "fans have"} this`,
    tracks: "Tracks",
    copied: "Copied",
    copyTitles: "Copy track list",
    interview: "Interviews",
    relatedPosts: "Related posts",
  },
  formats: {
    SG: "Single",
    配信SG: "Digital single",
    EP: "EP",
    miniAL: "Mini album",
    fullAL: "Album",
    "DVD/BD": "DVD/BD",
    MV: "MV",
    歌ってみた: "Cover",
    LP: "LP",
  },
  live: {
    metaDescription:
      "Reol's solo shows, tours and festival appearances, with setlists, reports and related posts for each show.",
    intro1: "Reol's solo shows, tours and festival appearances so far.",
    intro2:
      "Each show has its setlist, related posts and reports, so you can get a feel for the shows you couldn't make it to.",
    attendanceLabel: "Shows attended",
    attendanceHint: "Tap ✓ on a show card to mark it as attended (saved only in this browser)",
    heardLabel: "Songs heard live",
    heardHint: "Counted from the setlists of the shows you marked as attended (out of all songs ever performed live)",
    heardList: (n: number) => `Heard (${n})`,
    unheardList: (n: number) => `Not heard live yet (${n}, most played first)`,
    ongoing: "On now",
    upcoming: "Coming up",
    helpFilter: "Tap a hashtag at the top to filter the list",
    helpFilterExample: "e.g. tap “#れをる” to show only shows under the name れをる",
    helpDetailBefore: "Tap “",
    helpDetailAfter: " Details” to see the report, venue, setlist and related posts",
    tagEvent: "Event appearances",
    tagOneman: "Solo shows",
    count: (n: number) => `${n} ${n === 1 ? "item" : "items"}`,
    unknownYear: "Unknown",
    setlistCopyTitle: (title: string) => `${title} setlist`,
    markAttended: "Mark as attended",
    unmarkAttended: "Remove from attended",
    attended: "Attended",
    attendedInYear: (n: number) => `Attended ${n}`,
    showCount: (n: number) => `${n} ${n === 1 ? "show" : "shows"}`,
    songCount: (n: number) => `${n} ${n === 1 ? "song" : "songs"}`,
    detailTitle: "See the show page (setlist, venue, related posts)",
    detail: "Details",
    youtubePlaylist: "YouTube playlist",
    attendedCount: (n: number) => `${n} ${n === 1 ? "fan" : "fans"} attended`,
    setlist: "Setlist",
    copied: "Copied",
    copy: "Copy",
    songPageTitle: "See the song page",
    songStatsTitle: "See the performance history in Song Stats",
    noSetlist: "No setlist has been added yet",
    toShowPage: "See the show page",
    spoilerBadgeTitle: "This tour is ongoing — the setlist contains spoilers",
    spoilerBadge: "⚠ Spoilers",
    expand: "Expand",
    spoilerNoticeTitle: "⚠ Spoiler warning",
    spoilerNotice1: "This tour is currently ongoing.",
    spoilerNotice2: "The setlist and related posts contain spoilers.",
    spoilerNotice3: "Please continue only if you're OK with that.",
    showSpoiler: "Show it anyway",
    relatedPosts: "Related posts",
  },
  song: {
    shareText: (song: string) => `Reol “${song}”: live stats, MV and streaming links | !Legit (unofficial fan site)`,
    totalPlays: "PERFORMANCES",
    playsValue: (n: string) => n,
    firstPlayed: "FIRST PERFORMED",
    lastPlayed: "LAST PERFORMED",
    album: "Release:",
    lyrics: "Lyrics",
    music: "Music",
    stream: "Listen on streaming",
    lyricsLink: "Lyrics (Uta-Net, Japanese)",
    watchMv: "Watch the official MV",
    articles: "Read about this song (in Japanese)",
    shareTitle: "Share this song page on X",
    share: "Share on X",
    historyKicker: (n: string) => `PERFORMANCE HISTORY — ${n}`,
    noHistoryTitle: "No live performances recorded yet",
    noHistoryDesc: "When this song is performed live, it will show up here.",
    venueTbd: "Venue TBA",
    closeVideo: "Close",
    watchVideo: "Watch video",
    relatedPosts: "Related posts",
    toStatsTitle: "Song Stats",
    toStatsDesc: "Performance counts, debuts and latest performances for every song.",
    toSearchTitle: "Search",
    toSearchDesc: "Search songs, live shows and filming locations by keyword.",
    backToTop: "Back to top",
    metaLead: (song: string) => `Live performance stats for Reol's “${song}”.`,
    metaPlays: (n: number, first: string | null) =>
      ` Performed ${n} ${n === 1 ? "time" : "times"}, first on ${first ?? "an unknown date"}.`,
    metaNoPlays: " No live performances recorded.",
    metaTail: " Includes performance history, the official MV and streaming links.",
    breadcrumbStats: "Song Stats",
  },
  show: {
    setlistCopyTitle: (title: string) => `${title} setlist`,
    shareText: (heading: string) => `Reol “${heading}”: setlist and venue | !Legit (unofficial fan site)`,
    venue: "Venue",
    venueSite: "Venue website",
    reliveTitle: "Relive Player (beta) — play this setlist with your own local audio files",
    relive: "Relive Player (beta, bring your own audio)",
    shareTitle: "Share this show page on X",
    share: "Share on X",
    mapTitle: (heading: string) => `Venue map - ${heading}`,
    setlistKicker: (n: number) => `SETLIST — ${n} ${n === 1 ? "SONG" : "SONGS"}`,
    mcCount: (n: number) => ` (MC ${n})`,
    copied: "Copied",
    copy: "Copy",
    noSetlistTitle: "No setlist has been added yet",
    noSetlistDesc: "We'll add it once it's confirmed.",
    closeVideo: "Close",
    watchVideo: "Watch video",
    venueTbd: "Venue TBA",
    relatedPosts: "Related posts",
    toArchiveTitle: "Live archive",
    toArchiveDesc: "Setlists and details from every show.",
    toSearchTitle: "Search",
    toSearchDesc: "Search songs, live shows and filming locations by keyword.",
    backToTop: "Back to top",
    metaLead: (name: string) => `Reol live show: ${name}.`,
    metaVenue: (place: string) => ` Venue: ${place}.`,
    metaSetlist: (n: number) => ` Full setlist (${n} ${n === 1 ? "song" : "songs"}).`,
  },
  stats: {
    title: "Song Stats",
    metaDescription:
      "Live performance stats for every Reol song: total performances, first performance and most recent performance.",
    lead: "Total live performances of each song, with the dates it was first and most recently performed.",
    rulesLink: "How we count →",
    totalPlays: "Total performances",
    uniqueSongs: "Songs performed",
    searchPlaceholder: "🔍 Search by song title",
    sortPlays: "Most performed",
    sortLast: "Most recent",
    sortFirst: "Earliest debut",
    sortName: "Title",
    songCount: (n: string) => `${n} songs`,
    toSongPageTitle: "Go to song page",
    firstPlayed: "First",
    lastPlayed: "Latest",
    toAlbum: "View the release →",
    toSongPage: "Go to song page →",
    watchMv: "▶ Watch the official MV",
    stream: "♪ Listen on streaming",
    history: "Performance history",
    historyCount: (n: number) => `(${n})`,
    noMatch: "No songs match your search",
    backToTop: "← Back to top",
    historyLoadError: "Couldn't load the performance history",
    historyLoading: "Loading performance history...",
    historyEmpty: "No performance history",
  },
};

export default en;
